/**
 * THE CLIENT BOOK. Search, and a row per household.
 *
 * A row carries three facts and they are the three somebody actually needs while standing in a
 * driveway: who the household is, which town the home is in, and how they were found. The last of
 * those is priority 7 of the business brief and the reason `source_channel` is NOT NULL in the
 * schema: with a handful of clients it is the only honest read on which channel is worth money.
 *
 * AND THE TOWN COMES ON THE ROW, which it did not. The town lives on `sites` rather than on
 * `clients`, so this screen used to fill it in afterwards from GET /api/clients/:id, capped at
 * twenty-four, four at a time, cached by id for the life of the tab: two dozen extra requests to
 * read one word each, every time the book was opened, against a daily row budget the phone system
 * draws from as well. The real fix was one column on the list endpoint and it is there now, a
 * correlated subquery inside the one statement, so the book is one request and the town is drawn
 * with the row rather than arriving after it.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { humanise } from '@fk/shared';
import { ApiError, listClients, type ClientRow } from '../api.ts';
import { Card, MEASURE, Pill, ScreenTitle } from '../components/AppShell.tsx';
import { Busy, EmptyNotice, ErrorNotice, SavedNotice } from '../components/Notice.tsx';
import { clientStatusTone } from '../components/StatusPills.tsx';
import { sharingWords } from '../discard.ts';
import { CHANNEL_LABELS } from '../labels.ts';

export function ClientList() {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<readonly ClientRow[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  // What a page that sent somebody here said it did: a household forgotten (ops #82), whose own page
  // answers nothing any more, says so here.
  const sent = useLocation().state as {
    said?: unknown;
    sharing?: { count?: unknown; ids?: unknown } | null;
  } | null;
  const said = sent?.said;
  // And the enquiries that only share a number of theirs, which the forget left for an owner to
  // review by hand (decision A), each a link that opens it on the enquiries page.
  const listed: unknown = sent?.sharing?.ids;
  const sharingIds = Array.isArray(listed)
    ? listed.filter((id): id is string => typeof id === 'string')
    : [];
  const counted: unknown = sent?.sharing?.count;
  const sharingCount = typeof counted === 'number' ? counted : 0;

  /** Typing is not a request. A quarter second of quiet is, which is a lot less mobile data. */
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let live = true;
    setRows(null);
    setError(null);
    listClients(query)
      .then((answer) => {
        if (live) setRows(answer.clients);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'The client book did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [query, attempt]);

  return (
    <div>
      <ScreenTitle title="Clients" subtitle="Every household, how they were found, and where." />

      {typeof said === 'string' ? (
        <div className="mb-4">
          <SavedNotice>{said}</SavedNotice>
        </div>
      ) : null}
      {sharingCount > 0 ? (
        <div className="mb-4 text-sm">
          <p>{sharingWords(sharingCount)}</p>
          <ul className="mt-1 flex flex-wrap gap-x-4">
            {sharingIds.map((id, index) => (
              <li key={id}>
                <Link
                  to={`/enquiries?open=${encodeURIComponent(id)}`}
                  className="inline-flex min-h-11 items-center font-medium underline desk:min-h-9"
                >
                  Enquiry {index + 1}
                </Link>
              </li>
            ))}
            {sharingCount > sharingIds.length ? (
              <li className="inline-flex min-h-11 items-center desk:min-h-9">
                and {sharingCount - sharingIds.length} more on the enquiries page
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}

      <p className="mb-4 text-sm">
        <Link
          to="/enquiries"
          className="inline-flex min-h-11 items-center font-medium underline desk:min-h-9"
        >
          Enquiries: who has asked and not yet booked
        </Link>
      </p>

      {/* ONE ROW AT A DESK (ops #56). On a phone the box and the button are each the width of the
          hand, one above the other; from md the box takes the row and the button its own words,
          instead of a 1,500px gold bar across a monitor. */}
      <div className="mb-5 md:flex md:items-start md:gap-4">
        <div className="mb-4 md:mb-0 md:max-w-2xl md:grow">
          <label htmlFor="client-search" className="sr-only">
            Search households
          </label>
          <input
            id="client-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search households"
            autoComplete="off"
            className="block min-h-12 w-full rounded-lg border border-navy/25 bg-parchment px-3 py-2 desk:min-h-9 desk:py-1.5"
          />
          <p className={`mt-1 text-xs text-navy/65 ${MEASURE}`}>
            Looks at the household name and the source detail, which is where a Nextdoor thread or a
            realtor is written down.
          </p>
        </div>

        <Link
          to="/clients/new"
          className="flex min-h-12 w-full items-center justify-center rounded-lg border border-gold bg-gold px-5 text-base font-medium text-navy md:ml-auto md:w-auto md:shrink-0 desk:min-h-9 desk:px-4 desk:text-sm"
        >
          Type in a new client
        </Link>
      </div>

      {error ? (
        <ErrorNotice
          title="The client book did not load"
          message={error.message}
          onRetry={() => setAttempt((n) => n + 1)}
        />
      ) : rows === null ? (
        <Busy what="the client book" />
      ) : rows.length === 0 ? (
        query ? (
          <EmptyNotice title="Nothing matches that">
            <p>
              No household name or source detail contains {query}. Clear the box to see everybody.
            </p>
          </EmptyNotice>
        ) : (
          <EmptyNotice title="No clients yet">
            <p>
              The first one goes in here, with their phone number and their home in the same form.
              That is the whole of build step two.
            </p>
          </EmptyNotice>
        )
      ) : (
        /* A column of cards in a hand, and a grid of them at a desk (ops #56): one card the width
           of a monitor held three short lines and put eight households on a 1080 screen. */
        <ul className="grid gap-3 lg:grid-cols-2 2xl:grid-cols-3">
          {rows.map((row) => (
            <li key={row.id}>
              <Link to={`/clients/${row.id}`} className="block h-full">
                <Card className="h-full min-h-16">
                  <div className="flex items-start justify-between gap-3">
                    <h2 className="font-serif text-lg leading-tight">{row.displayName}</h2>
                    {/* Drawn only when it is not the ordinary one, and never coral: the tone
                        rule is the one in `components/StatusPills.tsx`, so the book and the
                        household's own page cannot come to say the same fact differently. */}
                    {row.status === 'active' ? null : (
                      <Pill tone={clientStatusTone(row.status)}>{humanise(row.status)}</Pill>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-navy/75">
                    {row.town ?? 'No home typed in for this household yet'}
                  </p>
                  <p className="mt-2">
                    <Pill tone="gold">Found through {CHANNEL_LABELS[row.sourceChannel]}</Pill>
                    {row.sourceDetail ? (
                      <span className="ml-2 text-xs text-navy/65">{row.sourceDetail}</span>
                    ) : null}
                  </p>
                </Card>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
